import {
	Fragment,
	createContext,
	forwardRef,
	useContext,
	useMemo
} from 'react';

import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { cva } from 'class-variance-authority';

import Plus from '@/icons/add.svg';
import ChevronDown from '@/icons/chevron-down.svg';
import Minus from '@/icons/minus.svg';
import { cn } from '@/utils';

const Context = createContext({});
const defaultVariants = {
	size: 'md'
};

const Accordion = forwardRef(({ children, size, isFaqs, ...props }, ref) => {
	const contextValue = useMemo(() => ({ size, isFaqs }), [size, isFaqs]);

	return (
		<Context.Provider value={contextValue}>
			<AccordionPrimitive.Root ref={ref} {...props}>
				{children}
			</AccordionPrimitive.Root>
		</Context.Provider>
	);
});

Accordion.displayName = 'Accordion';

const AccordionItem = forwardRef(({ className, ...props }, ref) => {
	const { isFaqs } = useContext(Context);

	return (
		<AccordionPrimitive.Item
			ref={ref}
			className={cn(
				'border-b',
				isFaqs
					? 'border-black data-[state=open]:border-red'
					: 'border-white',
				className
			)}
			{...props}
		/>
	);
});
AccordionItem.displayName = 'AccordionItem';

const triggerVariants = cva(
	'flex flex-1 items-start justify-between text-left transition-all [&[data-state=open]>svg]:rotate-180',
	{
		variants: {
			size: {
				md: 'h-10 py-2 md:py-7',
				sm: 'py-4'
			}
		},
		defaultVariants
	}
);

const AccordionTrigger = forwardRef(
	({ className, children, Component = Fragment, ...props }, ref) => {
		const { size, isFaqs } = useContext(Context);

		return (
			<AccordionPrimitive.Header
				className="flex"
				asChild={Component !== Fragment}
			>
				<Component>
					<AccordionPrimitive.Trigger
						ref={ref}
						className={cn(triggerVariants({ size }), className, 'group')}
						{...props}
					>
						{children}
						{isFaqs ? (
							<div className="flex shrink-0 items-start justify-start overflow-hidden rounded border border-grey-200 p-2">
								<Plus className="h-7 w-7 shrink-0 transition-transform duration-200 group-data-[state=open]:hidden" />

								<Minus className="h-7 w-7 shrink-0 transition-transform duration-200 group-data-[state=closed]:hidden" />
							</div>
						) : (
							<ChevronDown className="down m-2 h-6 w-6 shrink-0 p-1 text-red transition-transform duration-200" />
						)}
					</AccordionPrimitive.Trigger>
				</Component>
			</AccordionPrimitive.Header>
		);
	}
);
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const contentVariants = cva('', {
	variants: {
		size: {
			md: 'pb-6',
			sm: 'pb-5'
		}
	},
	defaultVariants
});

const AccordionContent = forwardRef(
	({ className, children, ...props }, ref) => {
		const { size } = useContext(Context);

		return (
			<AccordionPrimitive.Content
				ref={ref}
				className="overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
				{...props}
			>
				<div className={cn(contentVariants({ size }), className)}>
					{children}
				</div>
			</AccordionPrimitive.Content>
		);
	}
);

AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
